// Watching a source live with its sound (MCR monitoring, DECISIONS
// 2026-10-06): WebRTC from a node's monitor gateway, the picture at zero
// latency and one pair to listen to, switched live. Through central the
// offer goes to central, which adds TURN credentials for the node, and the
// browser takes central's TURN too; on a node opened directly it goes to the
// node. Used by the input's Live tab (MonitorPlayer) and the watch page.
import { onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { location as myLocation } from './location'
import { api, central, type Config, type Encode, type MonitorAnswer, type MonitorOfferBody, type Status } from './api'

export type MonitorState = 'idle' | 'connecting' | 'waiting' | 'live' | 'failed' | 'reconnecting' | 'locked'

// retryDelay is how long a picture that failed waits before it tries
// again, in ms: 2, 4, 8, 16 s, then every 30 s, for as long as it is on
// screen (MON-2: a wall on a TV, nobody at it, stayed failed after the
// node's upgrade in place, a relay's restart or a 30 s drop of the line)
export const retryDelay = (attempt: number) => Math.min(30, 2 ** (attempt + 1)) * 1000

// lockedRetry: a picture refused because monitoring is locked on the node
// (423, or its session ended with why) says so, not "reconnecting", and
// asks again this often, so a wall comes back by itself once it is unlocked
export const lockedRetry = 30_000

// stateTone is a picture's state line's colour: red for one that failed
// or retries, amber for monitoring locked (on purpose, not broken, never
// green), green when live
export function stateTone(s: MonitorState) {
    if (s === 'failed' || s === 'reconnecting') return 'text-bad'
    if (s === 'locked') return 'text-warn'
    return s === 'live' ? 'text-ok' : ''
}
// stopped: no picture coming (the Watch button is offered again)
export const stopped = (s: MonitorState) => s === 'idle' || s === 'failed' || s === 'reconnecting' || s === 'locked'

// the fields of a WebRTC stats report this reads
interface Stat {
    type: string
    kind?: string
    framesDecoded?: number
    nominated?: boolean
    state?: string
    currentRoundTripTime?: number
    localCandidateId?: string
    remoteCandidateId?: string
    candidateType?: string
}

// node: the node's id on central ('' for the node this page is on, directly);
// source: "input/<id>"; video: where the picture plays
export function useMonitor(node: Ref<string>, source: Ref<string>, video: Ref<HTMLVideoElement | null>) {
    const state = ref<MonitorState>('idle')
    const why = ref('')
    const pair = ref(0)
    const rtt = ref<number | null>(null)
    const route = ref('')
    const via = ref('') // "via relay CPH", or why the location's relay was not used
    let pc: RTCPeerConnection | null = null
    let dc: RTCDataChannel | null = null
    let session = ''
    let sessionNode = ''
    let sessionVia = '' // the relay the session is on (its location's)
    let timer: ReturnType<typeof setInterval> | undefined
    let retry: ReturnType<typeof setTimeout> | undefined // the next try after a failure
    let attempt = 0 // tries since the picture was last live
    let lost: ReturnType<typeof setTimeout> | undefined // a connection "disconnected" too long
    const stream = ref<MediaStream | null>(null)

    // fail tears the picture down and tries again a while later (retryDelay);
    // only stop() ends that
    function fail(msg: string) {
        teardown()
        why.value = msg
        state.value = 'reconnecting'
        retry = setTimeout(() => void connect(), retryDelay(attempt++))
    }

    // locked: monitoring is locked on the node (or on a relay, the origin's):
    // the reason shown, asked again every lockedRetry
    function locked(msg: string) {
        session = '' // ended on the node already, or never begun
        teardown()
        why.value = msg
        state.value = 'locked'
        retry = setTimeout(() => void connect(), lockedRetry)
    }

    async function start() {
        stop()
        await connect()
    }

    async function connect() {
        teardown()
        const nodeId = node.value
        state.value = 'connecting'
        why.value = ''
        try {
            // central's TURN for the browser's side too: a network that lets
            // nothing else out still reaches the node through it
            let iceServers: RTCIceServer[] = []
            if (nodeId) iceServers = (await central.turn().catch(() => null))?.ice_servers ?? []
            const conn = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' })
            pc = conn
            conn.addTransceiver('video', { direction: 'recvonly' })
            conn.addTransceiver('audio', { direction: 'recvonly' })
            dc = conn.createDataChannel('monitor')
            // the node ends a session with why ({"ended": "monitoring is locked …"})
            dc.onmessage = (m: MessageEvent) => {
                if (pc !== conn) return
                let ended = ''
                try {
                    ended = (JSON.parse(String(m.data)) as { ended?: string }).ended ?? ''
                } catch {
                    /* not one of the node's */
                }
                if (ended) locked(ended)
            }
            const ms = new MediaStream()
            stream.value = ms
            conn.ontrack = (e) => {
                ms.addTrack(e.track)
                if (video.value && video.value.srcObject !== ms) video.value.srcObject = ms
            }
            conn.onconnectionstatechange = () => {
                if (pc !== conn) return
                clearTimeout(lost)
                if (conn.connectionState === 'connected' && state.value === 'connecting') state.value = 'waiting'
                if (conn.connectionState === 'failed') {
                    fail('the connection to the node failed (its monitor port and central’s TURN unreachable?)')
                }
                if (conn.connectionState === 'disconnected') {
                    // a blip comes back by itself; one that lasts is a failure
                    lost = setTimeout(() => {
                        if (pc === conn && conn.connectionState === 'disconnected')
                            fail('the connection to the node dropped')
                    }, 10_000)
                }
            }
            await conn.setLocalDescription(await conn.createOffer())
            await gathered(conn)
            // on central, through this browser's location's relay (location.ts)
            const body: MonitorOfferBody = {
                source: source.value,
                sdp: conn.localDescription!.sdp,
                pair: pair.value,
                location: nodeId ? myLocation.value || undefined : undefined,
            }
            const ans: MonitorAnswer = nodeId ? await central.monitorOffer(nodeId, body) : await api.monitorOffer(body)
            if (pc !== conn) {
                // stopped meanwhile: let the node go too
                void hangUp(nodeId, ans.session, ans.via)
                return
            }
            via.value = ans.via ? `via relay ${ans.via_name || ans.via}` : (ans.fallback ?? '')
            sessionVia = ans.via ?? ''
            session = ans.session
            sessionNode = nodeId
            await conn.setRemoteDescription({ type: 'answer', sdp: ans.sdp })
            timer = setInterval(() => void measure(conn), 1000)
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e)
            if ((e as { status?: number }).status === 423) return locked(msg)
            // the node offline, its monitoring busy (its budget, a viewer
            // limit), too many offers: all of them pass, so try again
            fail(msg)
        }
    }

    const hangUp = (nodeId: string, s: string, through?: string) =>
        (nodeId ? central.monitorHangUp(nodeId, s, through) : api.monitorHangUp(s)).catch(() => {})

    // every candidate in the offer (no trickle through central and the
    // tunnel); a TURN server that does not answer holds gathering: go on
    // after 3 s
    function gathered(conn: RTCPeerConnection) {
        return new Promise<void>((resolve) => {
            if (conn.iceGatheringState === 'complete') return resolve()
            const t = setTimeout(resolve, 3000)
            conn.addEventListener('icegatheringstatechange', () => {
                if (conn.iceGatheringState === 'complete') {
                    clearTimeout(t)
                    resolve()
                }
            })
        })
    }

    async function measure(conn: RTCPeerConnection) {
        if (pc !== conn) return
        const rep = await conn.getStats()
        let frames = 0
        rep.forEach((v) => {
            const r = v as Stat
            if (r.type === 'inbound-rtp' && r.kind === 'video') frames = r.framesDecoded ?? 0
            if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') {
                if (typeof r.currentRoundTripTime === 'number') rtt.value = r.currentRoundTripTime * 1000
                const local = rep.get(r.localCandidateId ?? '') as Stat | undefined
                const remote = rep.get(r.remoteCandidateId ?? '') as Stat | undefined
                route.value =
                    local?.candidateType === 'relay' || remote?.candidateType === 'relay'
                        ? 'through central (TURN)'
                        : 'direct'
            }
        })
        if (state.value === 'waiting' && frames > 0) {
            state.value = 'live'
            attempt = 0
        }
    }

    // stop ends the picture and any try to come
    function stop() {
        clearTimeout(retry)
        retry = undefined
        attempt = 0
        teardown()
    }

    function teardown() {
        clearInterval(timer)
        clearTimeout(lost)
        const conn = pc
        pc = null
        dc = null
        if (conn) conn.close()
        if (session) {
            void hangUp(sessionNode, session, sessionVia)
            session = ''
            sessionVia = ''
        }
        if (video.value) video.value.srcObject = null
        stream.value = null
        rtt.value = null
        route.value = ''
        via.value = ''
        state.value = 'idle'
    }

    // the pair to hear, live on the data channel (-1: none)
    watch(pair, (k) => {
        if (dc?.readyState === 'open') dc.send(JSON.stringify({ pair: k }))
    })
    // a video element that appears after the tracks came (v-if) gets them
    watch(video, (v) => {
        if (v && stream.value && v.srcObject !== stream.value) v.srcObject = stream.value
    })
    // a page closed (a lid, a tab) says goodbye: its session and, a linger
    // later, the source's encode end now instead of when the node notices
    // the silence (MON-13); keepalive lets the request outlive the page
    const bye = () => {
        if (!session) return
        const s = session
        session = ''
        void (sessionNode ? central.monitorHangUp(sessionNode, s, sessionVia, true) : api.monitorHangUp(s, true)).catch(
            () => {},
        )
    }
    window.addEventListener('pagehide', bye)
    onBeforeUnmount(() => {
        window.removeEventListener('pagehide', bye)
        stop()
    })

    // what the state line says
    const label = () =>
        state.value === 'connecting'
            ? 'connecting…'
            : state.value === 'waiting'
              ? 'connected, waiting for a picture…'
              : state.value === 'live'
                ? 'live'
                : state.value === 'reconnecting'
                  ? `reconnecting (try ${attempt})… ${why.value}`
                  : why.value
    // the route as the viewer sees it: direct or through central's TURN to
    // the node or its location's relay
    const routeLabel = () => [route.value, via.value].filter(Boolean).join(' · ')
    return { state, why, pair, rtt, route, via, start, stop, label, routeLabel }
}

// the pairs a source has, for the listen choice (16 channels: 8 pairs)
export const pairsOf = (channels?: number) =>
    Array.from({ length: Math.max(1, Math.ceil((channels || 16) / 2)) }, (_, k) => k)

// A source's name and what its pairs are, for the pickers and the listen
// choice: an input's pairs are its channels; an encode's (and a
// destination's: its encode at its delay) are its audio tracks.
export interface SourceInfo {
    name: string
    kind: 'input' | 'encode' | 'destination'
    pairLabels: string[]
}
export function describeSource(cfg: Config | undefined, key: string, channels?: number): SourceInfo | undefined {
    const [kind, id] = key.split('/') as [string, string]
    const encodeLabels = (e: Encode) => e.audio.slice(0, 8).map((a, i) => a.name || `track ${i + 1}`)
    if (kind === 'input') {
        const i = cfg?.inputs.find((x) => x.id === id)
        if (!i) return undefined
        return { name: i.name, kind, pairLabels: pairsOf(channels).map((k) => `ch ${2 * k + 1}+${2 * k + 2}`) }
    }
    if (kind === 'encode') {
        const e = cfg?.encodes.find((x) => x.id === id)
        if (!e) return undefined
        return { name: e.name, kind, pairLabels: encodeLabels(e) }
    }
    if (kind === 'destination') {
        const d = cfg?.destinations.find((x) => x.id === id)
        const e = cfg?.encodes.find((x) => x.id === d?.encode_id)
        if (!d || !e) return undefined
        return {
            name: d.delay_seconds ? `${d.name} (${d.delay_seconds} s behind)` : d.name,
            kind,
            pairLabels: encodeLabels(e),
        }
    }
    return undefined
}

// a source's state and channel levels from its node's status: an encode's
// meters are its tracks' channels as its input has them (a destination's,
// its encode's)
export function sourceStatus(cfg: Config | undefined, st: Status | undefined, key: string) {
    const [kind, id] = key.split('/') as [string, string]
    if (!st) return undefined
    if (kind === 'input') {
        const s = st.inputs[id]
        return s && { state: s.state, peaks: s.peak_db ?? [] }
    }
    const encId = kind === 'encode' ? id : cfg?.destinations.find((d) => d.id === id)?.encode_id
    const e = cfg?.encodes.find((x) => x.id === encId)
    if (!e) return undefined
    const inPeaks = st.inputs[e.input_id]?.peak_db ?? []
    const peaks = e.audio.flatMap((a) => (a.channel_map ?? []).map((c) => inPeaks[c] ?? -100))
    const state = kind === 'encode' ? st.encodes[id]?.state : st.destinations[id]?.state
    return { state: state ?? '', peaks }
}

// the pickers' groups: what a node offers to watch
export function sourcesOf(cfg: Config | undefined) {
    if (!cfg) return []
    return [
        {
            label: 'Inputs',
            items: cfg.inputs.map((i) => ({
                key: 'input/' + i.id,
                name: i.name + (i.kind === 'multiview' ? ' · multiview' : ''),
            })),
        },
        {
            label: 'Encodes (what they send, decoded)',
            items: cfg.encodes.map((e) => ({ key: 'encode/' + e.id, name: e.name })),
        },
        {
            label: 'Destinations (what they send, at their delay)',
            items: cfg.destinations
                .filter((d) => !d.id.startsWith('_'))
                .map((d) => ({
                    key: 'destination/' + d.id,
                    name: d.delay_seconds ? `${d.name} (${d.delay_seconds} s behind)` : d.name,
                })),
        },
    ].filter((g) => g.items.length)
}
